import {
	FunctionComponent,
	ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import ReactDOM from "react-dom";

import { FeatureOnboardingPlacement } from "@/pages/Private/pages/FeatureOnboarding/enum/featureOnboarding.enum";
import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

interface SpotlightOverlayProps {
	target: Element;
	placement: FeatureOnboardingPlacement;
	children: ReactNode;
}

interface Position {
	top: number;
	left: number;
}

const GAP = 12;
const PADDING = 8;

export const SpotlightOverlay: FunctionComponent<SpotlightOverlayProps> = ({
	target,
	placement,
	children,
}) => {
	const popoverRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(popoverRef, true);
	const [cutoutRect, setCutoutRect] = useState<DOMRect | null>(null);
	const [popoverPos, setPopoverPos] = useState<Position>({ top: 0, left: 0 });

	const recalculate = useCallback(() => {
		const rect = target.getBoundingClientRect();

		setCutoutRect(rect);

		if (!popoverRef.current) {
			return;
		}

		const popoverRect = popoverRef.current.getBoundingClientRect();
		let top = 0;
		let left = 0;

		switch (placement) {
			case FeatureOnboardingPlacement.TOP:
				top = rect.top - popoverRect.height - GAP;
				left = rect.left + rect.width / 2 - popoverRect.width / 2;
				break;
			case FeatureOnboardingPlacement.BOTTOM:
				top = rect.bottom + GAP;
				left = rect.left + rect.width / 2 - popoverRect.width / 2;
				break;
			case FeatureOnboardingPlacement.LEFT:
				top = rect.top + rect.height / 2 - popoverRect.height / 2;
				left = rect.left - popoverRect.width - GAP;
				break;
			case FeatureOnboardingPlacement.RIGHT:
				top = rect.top + rect.height / 2 - popoverRect.height / 2;
				left = rect.right + GAP;
				break;
		}

		// Keep popover within viewport bounds. These are viewport coordinates for a
		// `position: fixed` node: adding window.scrollX/Y here would place an
		// absolutely positioned box past the viewport and grow the document, which
		// is page scroll on a shell that is meant to be exactly one viewport tall.
		const maxLeft = window.innerWidth - popoverRect.width - PADDING;
		const maxTop = window.innerHeight - popoverRect.height - PADDING;

		left = Math.max(PADDING, Math.min(left, maxLeft));
		top = Math.max(PADDING, Math.min(top, maxTop));

		setPopoverPos({ top, left });
	}, [target, placement]);

	// Scroll target into view on mount
	useEffect(() => {
		const rect = target.getBoundingClientRect();
		const isInViewport =
			rect.top >= 0 &&
			rect.bottom <= window.innerHeight &&
			rect.left >= 0 &&
			rect.right <= window.innerWidth;

		if (!isInViewport) {
			target.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [target]);

	// Recalculate position on mount and whenever target/placement changes
	useLayoutEffect(() => {
		recalculate();
	}, [recalculate]);

	// Recalculate on resize and scroll
	useEffect(() => {
		const handleUpdate = () => recalculate();

		window.addEventListener("resize", handleUpdate);
		window.addEventListener("scroll", handleUpdate, true);

		return () => {
			window.removeEventListener("resize", handleUpdate);
			window.removeEventListener("scroll", handleUpdate, true);
		};
	}, [recalculate]);

	const cutoutStyle = cutoutRect
		? {
				top: cutoutRect.top - PADDING,
				left: cutoutRect.left - PADDING,
				width: cutoutRect.width + PADDING * 2,
				height: cutoutRect.height + PADDING * 2,
			}
		: undefined;

	return ReactDOM.createPortal(
		<>
			{/* Full-screen backdrop */}
			<div className="fixed inset-0 z-[99998] bg-black/50" />

			{/* Cutout highlight around target */}
			{cutoutStyle && (
				<div
					className="fixed z-[99999] rounded-lg"
					style={{
						...cutoutStyle,
						boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
						pointerEvents: "none",
					}}
				/>
			)}

			{/* Popover */}
			<div
				ref={popoverRef}
				className="fixed z-[100000] w-[320px] rounded-xl bg-white p-5 shadow-lg"
				style={{
					top: popoverPos.top,
					left: popoverPos.left,
				}}
			>
				{children}
			</div>
		</>,
		document.body
	);
};
